import { read, TABLE } from '../data/db';
import { hypotheses, matches, talkPoints, teams } from '../data/repos';
import {
  byMatchHistory,
  byTalkPointDisplay,
  canSaveBoardAsTemplate,
  distillEntry,
  isDistillationInProgress,
  isFileStatus,
  isPendingDistillation,
  matchStateLabel,
} from '../domain/file';
import type { DistillEntry, FileStatus, PlayerLink } from '../domain/file';
import { ARTIFACT_PHASE } from '../types';
import type { ArtifactPhase, HypothesisRow, MatchRow, PlayerRow } from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';

/**
 * FILE-1 … FILE-3 / SES-11 — **the read half of Home and the opponent file** (build unit U-16).
 *
 * §1.2 forbids `ui/` from importing `data/`, so both screens get everything they render through
 * here. `domain/file.ts` owns every decision; this owns the transactions and the projection.
 *
 * ── The transaction SCOPE is a mechanism, the same one CARD-3 uses ──────────
 *
 * §10.4 names exactly what the opponent file lists: matches, their talk points, and the confirmed
 * and invalidated hypotheses across them, with a player row reachable from a hypothesis. Four
 * tables plus `teams`. **`observations`, `matchNotes`, `audioBlobs`, `flagResolutions` and
 * `logEntries` are absent from both scopes below**, and Dexie enforces a transaction's scope — a
 * read of any of them throws rather than returning rows.
 *
 * That is not tidiness. The opponent file is the natural home for "and while we're here, show the
 * flags" and "show what was recorded", and every one of those turns a scouting file into a capture
 * log the user has to scroll past to find the three sentences they came for. Making it a runtime
 * failure means the widening argument happens at review time rather than in a release.
 *
 * ── ENV-11 ──────────────────────────────────────────────────────────────────
 *
 * A `Player` reaches the screen as a {@link PlayerLink}, which has no field a name could occupy
 * (`domain/file.ts`) and no row id. The `PlayerRow` itself never leaves this module.
 */

const MODULE = 'app/home';

// ─────────────────────────────────────────────────────────────────────────────
// Home (FILE-1's team list + SES-11's queue)
// ─────────────────────────────────────────────────────────────────────────────

/** One match on Home, in either group. */
export interface QueuedMatch {
  readonly matchId: string;
  readonly teamId: string;
  readonly opponent: string;
  readonly date: string;
  readonly comp: string | null;
  readonly abandoned: boolean;
  readonly stateLabel: string;
  /** Never `null` here: both groups are built from states {@link distillEntry} answers for. */
  readonly entry: DistillEntry;
}

/** FILE-1 — *"Home lists Teams with match history."* */
export interface TeamCard {
  readonly teamId: string;
  readonly name: string;
  readonly club: string | null;
  readonly ageGroup: string | null;
  readonly matchCount: number;
  /** `null` for a team created but not yet played — PRE-1 allows one, and it is not an error. */
  readonly lastPlayed: string | null;
}

export interface HomeView {
  readonly teams: readonly TeamCard[];
  /** SES-11's queue, §10.4 verbatim: non-abandoned `fullTime`. Never nags; may be any length. */
  readonly pending: readonly QueuedMatch[];
  /** `triage`/`distill` — see `isDistillationInProgress` for why this is a second group. */
  readonly inProgress: readonly QueuedMatch[];
}

/**
 * Everything Home renders, from one readonly transaction.
 *
 * One, for `loadTriageContext`'s reason: the team list's match counts and the queue's membership
 * are two views of the same `matches` table, and a queue read after a count read could list a match
 * the counts above it do not include.
 *
 * **This is not on any capture path.** NFR-1's budget covers the tap→ack transaction; Home is
 * reached when no match is in progress (§2.5), so reading every match row is the honest way to
 * answer *"how many matches against this opponent"* without a denormalised counter that could drift.
 *
 * @throws {StorageError} on a read failure. Not swallowed: an empty Home and an unreadable Home
 *   look identical, and only one of them is the ordinary state of a fresh install.
 */
export async function loadHome(): Promise<HomeView> {
  return read(
    [TABLE.matches, TABLE.teams],
    async (): Promise<HomeView> => {
      const [teamRows, matchRows] = await Promise.all([teams.listTeams(), matches.listAllMatches()]);
      const nameOf = new Map(teamRows.map((row) => [row.id, row.name]));

      const queued = (row: MatchRow): QueuedMatch | null => {
        const entry = distillEntry(row);
        if (entry === null) return null;
        return {
          matchId: row.id,
          teamId: row.teamId,
          opponent: nameOf.get(row.teamId) ?? 'Opponent',
          date: row.date,
          comp: row.comp,
          abandoned: row.abandoned,
          stateLabel: matchStateLabel(row),
          entry,
        };
      };

      const inOrder = [...matchRows].sort(byMatchHistory);
      const group = (keep: (row: MatchRow) => boolean): QueuedMatch[] =>
        inOrder.filter(keep).map(queued).filter((item): item is QueuedMatch => item !== null);

      return {
        teams: teamRows.map((team) => {
          const played = matchRows.filter((row) => row.teamId === team.id);
          return {
            teamId: team.id,
            name: team.name,
            club: team.club,
            ageGroup: team.ageGroup,
            matchCount: played.length,
            // `byMatchHistory` is newest-first, so the head is the most recent.
            lastPlayed: [...played].sort(byMatchHistory)[0]?.date ?? null,
          };
        }),
        pending: group(isPendingDistillation),
        inProgress: group(isDistillationInProgress),
      };
    },
    MODULE,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The Team view (FILE-1, FILE-2, FILE-3, PRE-6)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * §10.4 — *"their archived talk points"*, and DIST-6's reason for existing.
 *
 * **Declared reading (§16.4).** "Archived talk points" is read as *every persisted `TalkPoint` of
 * the match, both phases, phase-labelled*. A `TalkPoint` only exists because the user promoted it,
 * `CompleteDistillation` is the only thing that archives a match, and a halftime adjustment is as
 * much a record of what was said as a full-time instruction is. Restricting the list to matches in
 * `archived` would hide the three instructions of a match still sitting in `distill` — the exact
 * matches Home is sending the user here to finish.
 */
export interface FileTalkPoint {
  readonly id: string;
  readonly phase: ArtifactPhase;
  readonly rank: number;
  /** INV-13's two arms resolved: a halftime row with no instruction reads as its source pattern. */
  readonly text: string;
  /**
   * DIST-6 — *"so the opponent file (FILE-1) can show which tracked patterns became instructions
   * across matches."* `null` when the talk point came from DIST-7's source-less escape hatch.
   */
  readonly sourceText: string | null;
}

export interface FileMatch {
  readonly matchId: string;
  readonly date: string;
  readonly comp: string | null;
  readonly stateLabel: string;
  readonly abandoned: boolean;
  /** `null` for a match that is running, unstarted or already archived. */
  readonly entry: DistillEntry | null;
  /** PRE-6's window — see `canSaveBoardAsTemplate`. */
  readonly canSaveTemplate: boolean;
  /** How many patterns a PRE-6 template would carry. `0` is why the action can be present and inert. */
  readonly boardSize: number;
  readonly talkPoints: readonly FileTalkPoint[];
}

/** FILE-1 as amended by §19 F-20, with FILE-2's linkage attached. */
export interface FileHypothesis {
  readonly hypothesisId: string;
  readonly text: string;
  readonly status: FileStatus;
  readonly matchId: string;
  readonly matchDate: string;
  /** FILE-2 — the `Player` a shirt-number slot bound to (PRE-4), or `null`. */
  readonly player: PlayerLink | null;
}

export interface TeamFile {
  readonly teamId: string;
  readonly name: string;
  readonly club: string | null;
  readonly ageGroup: string | null;
  readonly matches: readonly FileMatch[];
  readonly hypotheses: readonly FileHypothesis[];
}

/**
 * The whole opponent file, from one readonly transaction.
 *
 * @returns `undefined` when the team no longer exists — a navigation outcome, not an error: the
 *   Team view is reached from a Home list that may have been read before an INV-8 cascade.
 * @throws {StorageError} on a read failure.
 */
export async function loadTeamFile(teamId: string): Promise<TeamFile | undefined> {
  return read(
    [TABLE.teams, TABLE.players, TABLE.matches, TABLE.hypotheses, TABLE.talkPoints],
    async (): Promise<TeamFile | undefined> => {
      const team = await teams.getTeam(teamId);
      if (team === undefined) return undefined;

      const [matchRows, playerRows] = await Promise.all([
        matches.listMatchesForTeam(teamId),
        teams.listPlayersForTeam(teamId),
      ]);
      const history = [...matchRows].sort(byMatchHistory);
      const linkOf = new Map<string, PlayerLink>(
        playerRows.map((row) => [row.id, toPlayerLink(row)]),
      );

      const fileMatches: FileMatch[] = [];
      const fileHypotheses: FileHypothesis[] = [];

      for (const row of history) {
        const patterns = await hypotheses.listHypothesesForMatch(row.id);
        const textOf = new Map(patterns.map((pattern) => [pattern.id, pattern.text]));
        const points = await talkPoints.listForMatch(row.id);

        fileMatches.push({
          matchId: row.id,
          date: row.date,
          comp: row.comp,
          stateLabel: matchStateLabel(row),
          abandoned: row.abandoned,
          entry: distillEntry(row),
          canSaveTemplate: canSaveBoardAsTemplate(row.state),
          boardSize: patterns.filter((pattern) => pattern.boardPosition !== null).length,
          talkPoints: points
            .map((point) => ({
              id: point.id,
              phase: point.phase,
              rank: point.rank,
              text: talkPointText(
                point.phase,
                point.instructionText,
                point.sourceHypothesisId,
                textOf,
              ),
              sourceText:
                point.sourceHypothesisId === null
                  ? null
                  : (textOf.get(point.sourceHypothesisId) ?? null),
            }))
            // `listForMatch` sorts by `rank`, which is total within a phase and not across them.
            // See `byTalkPointDisplay` — the Team view is the first surface to render both.
            .sort(byTalkPointDisplay),
        });

        for (const pattern of patterns) {
          if (!isFileStatus(pattern.status)) continue;
          fileHypotheses.push({
            hypothesisId: pattern.id,
            text: pattern.text,
            status: pattern.status,
            matchId: row.id,
            matchDate: row.date,
            player: playerLinkFor(pattern, linkOf),
          });
        }
      }

      return {
        teamId: team.id,
        name: team.name,
        club: team.club,
        ageGroup: team.ageGroup,
        matches: fileMatches,
        hypotheses: fileHypotheses,
      };
    },
    MODULE,
  );
}

/**
 * ENV-11 — the projection, and the **only** place a `PlayerRow` becomes something a screen can see.
 *
 * `id` is dropped here rather than by the caller: a projection the caller performs is a projection
 * the next caller can skip.
 */
function toPlayerLink(row: PlayerRow): PlayerLink {
  return { number: row.number, foot: row.foot, notes: row.notes };
}

/**
 * FILE-2 — *"hypotheses referencing a shirt number link to the Player."*
 *
 * A `playerId` that names no row resolves to `null` rather than throwing: INV-8's cascade removes
 * players with their team, so the only way to reach this is a partially-restored archive (SYN-5),
 * and a Team view that refused to render because one pattern lost its player would take the whole
 * opponent file down with it.
 */
function playerLinkFor(
  row: HypothesisRow,
  links: ReadonlyMap<string, PlayerLink>,
): PlayerLink | null {
  if (row.playerId === null) return null;
  return links.get(row.playerId) ?? null;
}

/**
 * INV-13's two arms, resolved at load — `loadCardContent`'s rule applied to the file screen.
 *
 * A `fullTime` row's `instructionText` is `string` by construction. Only the halftime arm can be
 * `null`, and INV-13 says why that is legal: *"a halftime adjustment is usable with nothing but its
 * source hypothesis text."*
 */
function talkPointText(
  phase: ArtifactPhase,
  instructionText: string | null,
  sourceHypothesisId: string | null,
  texts: ReadonlyMap<string, string>,
): string {
  if (phase === ARTIFACT_PHASE.fullTime) return instructionText ?? '';
  if (instructionText !== null && instructionText.trim() !== '') return instructionText;
  if (sourceHypothesisId === null) return '';
  return texts.get(sourceHypothesisId) ?? '';
}

// ─────────────────────────────────────────────────────────────────────────────
// PRE-6
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PRE-6 — *"Any match's board is savable as a named user template post-match."*
 *
 * The command carries the id and the name and **nothing else**: the entries are derived inside the
 * transaction from the match's own positioned hypotheses, so *"from that match's positioned
 * hypotheses"* is a property of the handler rather than of what this screen happened to send.
 */
export function saveBoardAsTemplate(
  matchId: string,
  templateId: string,
  name: string,
): Promise<DispatchResult> {
  return dispatch({ type: 'SaveBoardAsTemplate', matchId, templateId, name });
}
